import { useEffect, useMemo } from "react";

import { Alert, CircularProgress } from "@mui/material";

import { useAppDispatch, useAppSelector } from "app/hooks";

import {
    useCreateDiagnosticsSessionMutation,
    useExtendDiagnosticsSessionQuery,
} from "services/streamingApi/streamingPodManager.api";

import { diagnosticsSessionSelectors } from "./store/selectors.session";
import { diagnosticsActions } from "./store/slice";
import { DiagnosticsConfiguration } from "types/AppConfig";
import { DiagnosticsPlayer } from "./DiagnosticsPlayer";
import { Box } from "@mui/material";
import { DiagnosticsMetrics } from "./DiagnosticsMetrics";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";

export interface DiagnosticsSessionProps {
    drawerWidth: number;
    diagnosticsConfiguration: DiagnosticsConfiguration;
}

export function DiagnosticsSession(props: DiagnosticsSessionProps) {
    const dispatch = useAppDispatch();

    const sessionId = useAppSelector(
        diagnosticsSessionSelectors.selectSessionId,
    );
    const appInsights = useAppInsightsContext();

    // create session per selected site
    const [createDiagnosticsSession, { isLoading, isError, isSuccess }] =
        useCreateDiagnosticsSessionMutation();

    useEffect(() => {
        let abortRequestCallback: () => void;

        const createSessionId = async () => {
            const request = createDiagnosticsSession(undefined);
            abortRequestCallback = request.abort;

            const sessionId = await request.unwrap();
            dispatch(diagnosticsActions.setSessionId({ sessionId }));
        };

        createSessionId();

        return () => abortRequestCallback && abortRequestCallback();
    }, [createDiagnosticsSession, dispatch]);

    // reset session id when unmounting this component
    useEffect(() => {
        return () => {
            dispatch(diagnosticsActions.resetSessionId());
        };
    }, [dispatch]);

    const operationId = useMemo(() => {
        if (sessionId) {
            return appInsights.core.getTraceCtx()?.getTraceId();
        }

        return undefined;
    }, [sessionId]);

    // keep the session alive while this component is on screen
    useExtendDiagnosticsSessionQuery(
        {
            sessionId: sessionId ?? "",
            extendBy: props.diagnosticsConfiguration.extendSessionBy,
        },
        {
            skip: !sessionId,
            pollingInterval:
                props.diagnosticsConfiguration.extendSessionPollingIntervalInMs,
            // prevent rerendering by returning an empty object
            selectFromResult: () => ({}),
        },
    );

    return (
        <>
            {isLoading && <CircularProgress />}
            {isError && (
                <Alert severity="error">
                    Error creating diagnostic session
                </Alert>
            )}
            {isSuccess && sessionId && (
                <Box>
                    <DiagnosticsPlayer
                        sessionId={sessionId}
                        operationId={operationId}
                    />

                    <DiagnosticsMetrics diagnosticsCorrelationId={sessionId} />
                </Box>
            )}
        </>
    );
}
